import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  useTheme, 
  alpha,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  Tooltip,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid as MuiGrid,
  Collapse
} from '@mui/material';
import { 
  Cloud as CloudIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
  Computer as InstanceIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Storage as StorageIcon,
  DataUsage as MemoryIcon,
  Memory as CpuIcon,
  AttachMoney as CostIcon,
  Info as InfoIcon,
  ViewModule as GridViewIcon,
  ViewList as ListViewIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  CloudDownload as DownloadIcon,
  ShowChart as ShowChartIcon
} from '@mui/icons-material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { format, subDays } from 'date-fns';
import { Bar, Pie } from 'react-chartjs-2';

// Custom components
import PageHeader from '../components/common/PageHeader';
import LoadingOverlay from '../components/common/LoadingOverlay';
import EmptyState from '../components/common/EmptyState';
import api from '../services/api';

// Helper function to format currency
const formatCurrency = (value) => {
    if (value === null || value === undefined) return '₹0.00';
    return `₹${value.toLocaleString(undefined, { 
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };
// Helper function to determine status color
const getStatusColor = (status) => {
  switch (status) {
    case 'RUNNING':
      return 'success';
    case 'STOPPED':
      return 'warning';
    case 'TERMINATED':
      return 'error';
    default:
      return 'default';
  }
};

// Helper function for date formatting
const formatDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) return '';
  if (startDate === endDate) return `on ${startDate}`;
  return `from ${startDate} to ${endDate}`;
};

const OCIInstances = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  
  // State for filters
  const [region, setRegion] = useState(searchParams.get('region') || '');
  const [compartment, setCompartment] = useState(searchParams.get('compartment') || '');
  const [startDate, setStartDate] = useState(subDays(new Date(), 7));
  const [endDate, setEndDate] = useState(new Date());
  const [searchTerm, setSearchTerm] = useState('');
  const [regions, setRegions] = useState([]);
  const [compartments, setCompartments] = useState([]);
  
  // Freeform tag filter state
  const [tagFilters, setTagFilters] = useState({});
  const [newTagKey, setNewTagKey] = useState('');
  const [newTagValue, setNewTagValue] = useState('');
  const [isTagFilterOpen, setIsTagFilterOpen] = useState(false);
  const tagKeyInputRef = React.useRef(null);
  
  // State for view options
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'grid'
  
  // State for pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Data state
  const [instances, setInstances] = useState([]);
  
  // Loading state
  const [loading, setLoading] = useState({
    regions: false,
    compartments: false,
    instances: false
  });
  
  // Format date for API requests
  const formatDateForAPI = (date) => {
    return format(date, 'yyyy-MM-dd');
  };
  
  // Fetch regions data
  const fetchRegions = async () => {
    setLoading(prev => ({ ...prev, regions: true }));
    try {
      const response = await api.get('/api/oci/regions');
      setRegions(response.data || []);
    } catch (error) {
      console.error('Error fetching regions:', error);
    } finally {
      setLoading(prev => ({ ...prev, regions: false }));
    }
  };
  
  // Fetch compartments data
  const fetchCompartments = async (selectedRegion) => {
    setLoading(prev => ({ ...prev, compartments: true }));
    try {
      const url = selectedRegion 
        ? `/api/oci/compartments?region=${selectedRegion}`
        : '/api/oci/compartments';
      
      const response = await api.get(url);
      setCompartments(response.data || []);
    } catch (error) {
      console.error('Error fetching compartments:', error);
    } finally {
      setLoading(prev => ({ ...prev, compartments: false }));
    }
  };
  
  // Fetch instances data
  const fetchInstances = async () => {
    setLoading(prev => ({ ...prev, instances: true }));
    try {
      const params = new URLSearchParams();
      if (region) params.append('region', region);
      if (compartment) params.append('compartment', compartment);
      params.append('start_date', formatDateForAPI(startDate));
      params.append('end_date', formatDateForAPI(endDate));
      params.append('date_range', 'true');
      
      // Add tag filters to the request
      if (Object.keys(tagFilters).length > 0) {
        params.append('tag_filters', JSON.stringify(tagFilters));
      }
      
      console.log('Fetching instances with params:', params.toString());
      console.log('Current tag filters:', tagFilters);
      
      const response = await api.get(`/api/oci/instances?${params.toString()}`);
      
      console.log(`Received ${response.data?.length || 0} instances from server`);
      
      setInstances(response.data || []);
    } catch (error) {
      console.error('Error fetching instances:', error);
    } finally {
      setLoading(prev => ({ ...prev, instances: false }));
    }
  };
  
  // Load data
  const loadData = () => {
    fetchRegions();
    fetchCompartments(region);
    fetchInstances();
  };
  
  // Initialize data load
  useEffect(() => {
    loadData();
  }, []);
  
  // Update compartments when region changes
  useEffect(() => {
    fetchCompartments(region);
    
    // Update URL to reflect filter changes
    const searchParams = new URLSearchParams();
    if (region) searchParams.set('region', region);
    if (compartment) searchParams.set('compartment', compartment);
    
    const newSearch = searchParams.toString() ? `?${searchParams.toString()}` : '';
    navigate(`${location.pathname}${newSearch}`, { replace: true });
  }, [region]);
  
  // Update instances when filters change
  useEffect(() => {
    fetchInstances();
    
    // Update URL to reflect filter changes
    const searchParams = new URLSearchParams();
    if (region) searchParams.set('region', region);
    if (compartment) searchParams.set('compartment', compartment);
    
    const newSearch = searchParams.toString() ? `?${searchParams.toString()}` : '';
    navigate(`${location.pathname}${newSearch}`, { replace: true });
  }, [region, compartment, startDate, endDate, tagFilters]);
  
  // Handle refresh
  const handleRefresh = () => {
    loadData();
  };
  
  // Handle pagination changes
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Tag filter handlers
  const handleAddTagFilter = () => {
    if (newTagKey && newTagValue) {
      setTagFilters(prev => ({
        ...prev,
        [newTagKey]: newTagValue
      }));
      setNewTagKey('');
      setNewTagValue('');
      // Maintain focus on the key input after adding a tag
      setTimeout(() => {
        tagKeyInputRef.current?.focus();
      }, 0);
    }
  };

  const handleRemoveTagFilter = (key) => {
    setTagFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[key];
      return newFilters;
    });
    // Maintain focus on the key input after removing a tag
    setTimeout(() => {
      tagKeyInputRef.current?.focus();
    }, 0);
  };

  // Filter instances based on all criteria including tags
  const filteredInstances = useMemo(() => {
    console.log('Filtering instances:', instances.length);
    const filtered = instances.filter(instance => {
      // Apply search term filter only (since tags are now filtered on server)
      const searchLower = searchTerm.toLowerCase();
      return !searchTerm || 
        instance.instance_name.toLowerCase().includes(searchLower) ||
        instance.instance_id.toLowerCase().includes(searchLower) ||
        instance.region_name.toLowerCase().includes(searchLower) ||
        instance.compartment_name.toLowerCase().includes(searchLower) ||
        instance.vm_shape?.toLowerCase().includes(searchLower) ||
        instance.created_by?.toLowerCase().includes(searchLower);
    });
    console.log('Filtered instances:', filtered.length);
    return filtered;
  }, [instances, searchTerm]);
  
  // Paginated instances
  const paginatedInstances = useMemo(() => {
    return filteredInstances.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredInstances, page, rowsPerPage]);
  
  // Aggregate stats
  const stats = useMemo(() => {
    if (!filteredInstances.length) return { 
      total: 0, 
      running: 0, 
      stopped: 0, 
      totalCost: 0,
      totalCPU: 0,
      totalMemory: 0,
      avgDailyCost: 0,
      dateRange: `${formatDateForAPI(startDate)} to ${formatDateForAPI(endDate)}`,
      dayCount: Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1
    };
    
    const result = filteredInstances.reduce((acc, instance) => {
      // Count by status
      if (instance.instance_state === 'RUNNING') acc.running++;
      else if (instance.instance_state === 'STOPPED') acc.stopped++;
      
      // Sum costs and resources
      acc.totalCost += instance.total_cost || 0;
      acc.totalCPU += instance.cpu || 0;
      acc.totalMemory += instance.memory || 0;
      
      return acc;
    }, { 
      total: filteredInstances.length, 
      running: 0, 
      stopped: 0, 
      totalCost: 0,
      totalCPU: 0,
      totalMemory: 0,
      dateRange: `${formatDateForAPI(startDate)} to ${formatDateForAPI(endDate)}`,
      dayCount: Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1
    });
    
    // Calculate average daily cost
    result.avgDailyCost = result.totalCost / result.dayCount;
    
    return result;
  }, [filteredInstances, startDate, endDate, formatDateForAPI]);
  
  // Handle download report
  const handleDownloadReport = useCallback(() => {
    // Only proceed if there's data to export
    if (!filteredInstances.length) return;
    
    try {
      // Format date for filename
      const currentDate = new Date().toISOString().split('T')[0];
      
      // Create CSV header row
      const headers = [
        'Instance Name',
        'Region',
        'Compartment',
        'Status',
        'Shape',
        'CPUs',
        'Memory (GB)',
        'Boot Volume (GB)',
        'Total Cost',
        'Daily Avg Cost',
        'Compute Cost',
        'Boot Volume Cost',
        'Days with Data',
        'Created By',
        'Date Range'
      ];
      
      // Format data for CSV
      const csvData = filteredInstances.map(instance => ([
        instance.instance_name || '',
        instance.region_name || '',
        instance.compartment_name || '',
        instance.instance_state || '',
        instance.vm_shape || '',
        instance.cpu || 0,
        instance.memory ? instance.memory.toFixed(1) : 0,
        instance.boot_volume_size || 0,
        instance.total_cost || 0,
        instance.daily_avg_cost || 0,
        instance.compute_cost || 0,
        instance.boot_volume_price || 0,
        instance.days_with_data || 0,
        instance.created_by || '',
        `${instance.date_range?.start_date} to ${instance.date_range?.end_date}`
      ]));
      
      // Convert to CSV format
      let csvContent = [
        headers.join(','),
        ...csvData.map(row => row.map(cell => {
          // Wrap strings containing commas in quotes
          if (typeof cell === 'string' && cell.includes(',')) {
            return `"${cell}"`;
          }
          return cell;
        }).join(','))
      ].join('\n');
      
      // Create download link
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      
      // Set link properties
      link.setAttribute('href', url);
      link.setAttribute('download', `oci-instances-report-${currentDate}.csv`);
      link.style.visibility = 'hidden';
      
      // Add to document, click and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log('Report downloaded successfully');
    } catch (error) {
      console.error('Error generating report:', error);
      // You could add a snackbar/toast notification here
    }
  }, [filteredInstances]);
  
  // Handle download cost trend
  const handleDownloadCostTrend = useCallback(() => {
    // Only proceed if there's data to export
    if (!filteredInstances.length) return;
    
    try {
      // Format date for filename
      const currentDate = new Date().toISOString().split('T')[0];
      const dateRange = `${formatDateForAPI(startDate)}-${formatDateForAPI(endDate)}`;
      
      // Collect all unique dates across all instances
      const allDates = new Set();
      const instancesWithCosts = {};
      
      // First collect all dates and organize costs by instance name
      filteredInstances.forEach(instance => {
        if (!instance.daily_costs || instance.daily_costs.length === 0) return;
        
        instancesWithCosts[instance.instance_name] = {};
        
        instance.daily_costs.forEach(day => {
          allDates.add(day.date);
          instancesWithCosts[instance.instance_name][day.date] = day.cost;
        });
      });
      
      // Sort dates chronologically
      const sortedDates = Array.from(allDates).sort();
      
      // Create header row - Date + all instance names
      const headers = ['Date', ...Object.keys(instancesWithCosts)];
      
      // Create one row per date with costs for each instance
      const rows = sortedDates.map(date => {
        const row = [date];
        
        // Add cost for each instance on this date (or 0 if no data)
        Object.keys(instancesWithCosts).forEach(instanceName => {
          row.push(instancesWithCosts[instanceName][date] || 0);
        });
        
        return row;
      });
      
      // Convert to CSV format
      let csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => {
          // Wrap strings containing commas in quotes
          if (typeof cell === 'string' && cell.includes(',')) {
            return `"${cell}"`;
          }
          return cell;
        }).join(','))
      ].join('\n');
      
      // Create download link
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      
      // Set link properties
      link.setAttribute('href', url);
      link.setAttribute('download', `oci-instances-cost-trend-${dateRange}.csv`);
      link.style.visibility = 'hidden';
      
      // Add to document, click and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log('Cost trend data downloaded successfully');
    } catch (error) {
      console.error('Error generating cost trend data:', error);
      // You could add a snackbar/toast notification here
    }
  }, [filteredInstances, startDate, endDate, formatDateForAPI]);
  
  // State for detailed cost dialog
  const [selectedInstance, setSelectedInstance] = useState(null);
  const [costDialogOpen, setCostDialogOpen] = useState(false);

  // Handle opening cost detail dialog
  const handleOpenCostDialog = (instance) => {
    setSelectedInstance(instance);
    setCostDialogOpen(true);
  };

  // Handle closing cost detail dialog
  const handleCloseCostDialog = () => {
    setSelectedInstance(null);
    setCostDialogOpen(false);
  };

  // Cost Details Dialog
  const CostDetailsDialog = () => {
    if (!selectedInstance) return null;

    // Prepare daily cost data for chart
    const dailyCosts = selectedInstance.daily_costs || [];
    dailyCosts.sort((a, b) => a.date.localeCompare(b.date));
    
    const chartData = {
      labels: dailyCosts.map(d => d.date),
      datasets: [
        {
          label: 'Daily Cost',
          data: dailyCosts.map(d => d.cost),
          backgroundColor: theme.palette.primary.main,
          borderColor: theme.palette.primary.dark,
          borderWidth: 1,
        }
      ]
    };

    // Chart options
    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: 'Cost (₹)'
          }
        },
        x: {
          title: {
            display: true,
            text: 'Date'
          }
        }
      },
      plugins: {
        tooltip: {
          callbacks: {
            label: function(context) {
              let label = context.dataset.label || '';
              if (label) {
                label += ': ';
              }
              if (context.parsed.y !== null) {
                label += formatCurrency(context.parsed.y);
              }
              return label;
            }
          }
        }
      }
    };

    // Cost breakdown data for pie chart
    const costBreakdownData = {
      labels: ['Compute', 'Boot Volume'],
      datasets: [
        {
          data: [
            selectedInstance.compute_cost || 0,
            selectedInstance.boot_volume_price || 0
          ],
          backgroundColor: [
            theme.palette.primary.main,
            theme.palette.secondary.main
          ],
          borderWidth: 1
        }
      ]
    };

    // Pie chart options
    const pieOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom'
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              const label = context.label || '';
              const value = formatCurrency(context.raw);
              const total = selectedInstance.compute_cost + selectedInstance.boot_volume_price;
              const percentage = total > 0 ? ((context.raw / total) * 100).toFixed(1) : '0.0';
              return `${label}: ${value} (${percentage}%)`;
            }
          }
        }
      }
    };

    return (
      <Dialog
        open={costDialogOpen}
        onClose={handleCloseCostDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">
              Cost Analysis: {selectedInstance.instance_name}
            </Typography>
            <Box>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={() => handleDownloadInstanceCostDetails(selectedInstance)}
                sx={{
                  mr: 1,
                  borderRadius: '8px',
                  textTransform: 'none',
                }}
              >
                Export Data
              </Button>
              <IconButton edge="end" color="inherit" onClick={handleCloseCostDialog} aria-label="close">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <MuiGrid container spacing={3}>
            {/* Cost summary */}
            <MuiGrid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Cost Summary {formatDateRange(selectedInstance.date_range?.start_date, selectedInstance.date_range?.end_date)}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                Data available for {selectedInstance.days_with_data} out of {selectedInstance.date_range?.days_count} days
              </Typography>
              
              <MuiGrid container spacing={2}>
                <MuiGrid item xs={12} sm={4}>
                  <Paper sx={{ p: 2, bgcolor: 'primary.ultralight', height: '100%' }}>
                    <Typography variant="caption" color="text.secondary">Total Cost</Typography>
                    <Typography variant="h6" sx={{ mt: 0.5, fontWeight: 600 }}>
                      {formatCurrency(selectedInstance.total_cost)}
                    </Typography>
                  </Paper>
                </MuiGrid>
                <MuiGrid item xs={12} sm={4}>
                  <Paper sx={{ p: 2, bgcolor: 'primary.ultralight', height: '100%' }}>
                    <Typography variant="caption" color="text.secondary">Daily Average</Typography>
                    <Typography variant="h6" sx={{ mt: 0.5, fontWeight: 600 }}>
                      {formatCurrency(selectedInstance.daily_avg_cost)}
                    </Typography>
                  </Paper>
                </MuiGrid>
                <MuiGrid item xs={12} sm={4}>
                  <Paper sx={{ p: 2, bgcolor: 'primary.ultralight', height: '100%' }}>
                    <Typography variant="caption" color="text.secondary">Instance Details</Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      Shape: {selectedInstance.vm_shape}
                    </Typography>
                    <Typography variant="body2">
                      CPU: {selectedInstance.cpu} cores
                    </Typography>
                    <Typography variant="body2">
                      Memory: {selectedInstance.memory.toFixed(1)} GB
                    </Typography>
                  </Paper>
                </MuiGrid>
              </MuiGrid>
            </MuiGrid>

            {/* Daily cost trend */}
            {selectedInstance.daily_costs && selectedInstance.daily_costs.length > 0 && (
              <MuiGrid item xs={12} md={8}>
                <Typography variant="subtitle1" gutterBottom>
                  Daily Cost Trend
                </Typography>
                <Paper sx={{ p: 2, height: 300 }}>
                  {dailyCosts.length > 0 ? (
                    <Bar 
                      data={chartData} 
                      options={chartOptions}
                    />
                  ) : (
                    <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                      <Typography variant="body2" color="text.secondary">
                        No daily cost data available
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </MuiGrid>
            )}

            {/* Cost breakdown pie chart */}
            <MuiGrid item xs={12} md={4}>
              <Typography variant="subtitle1" gutterBottom>
                Cost Breakdown
              </Typography>
              <Paper sx={{ p: 2, height: 300, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                {(selectedInstance.compute_cost > 0 || selectedInstance.boot_volume_price > 0) ? (
                  <Pie 
                    data={costBreakdownData}
                    options={pieOptions}
                  />
                ) : (
                  <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                    <Typography variant="body2" color="text.secondary">
                      No cost breakdown data available
                    </Typography>
                  </Box>
                )}
              </Paper>
            </MuiGrid>
          </MuiGrid>
        </DialogContent>
      </Dialog>
    );
  };

  // Handle download instance cost details
  const handleDownloadInstanceCostDetails = useCallback((instance) => {
    if (!instance) return;
    
    try {
      // Format date for filename
      const currentDate = new Date().toISOString().split('T')[0];
      
      // Extract instance data
      const {
        instance_name,
        region_name,
        compartment_name,
        vm_shape,
        cpu,
        memory,
        instance_state,
        boot_volume_size,
        compute_cost,
        boot_volume_price,
        total_cost,
        daily_avg_cost,
        days_with_data,
        created_by,
        date_range,
        daily_costs = []
      } = instance;
      
      // Format date range for filename
      const dateRangeStr = date_range ? 
        `${date_range.start_date.replace(/-/g, '')}_${date_range.end_date.replace(/-/g, '')}` : 
        currentDate;
      
      // Create summary section
      const summary = [
        ['Instance Cost Summary'],
        ['Instance Name', instance_name || ''],
        ['Region', region_name || ''],
        ['Compartment', compartment_name || ''],
        ['Shape', vm_shape || ''],
        ['CPU', cpu || '0'],
        ['Memory', memory ? `${memory.toFixed(1)} GB` : '0 GB'],
        ['Boot Volume Size', boot_volume_size ? `${boot_volume_size} GB` : '0 GB'],
        ['Status', instance_state || ''],
        ['Created By', created_by || ''],
        [''],
        ['Cost Analysis'],
        ['Date Range', date_range ? `${date_range.start_date} to ${date_range.end_date}` : ''],
        ['Days in Range', date_range ? date_range.days_count : '0'],
        ['Days with Data', days_with_data || '0'],
        ['Total Cost', total_cost || '0'],
        ['Daily Average Cost', daily_avg_cost || '0'],
        ['Compute Cost', compute_cost || '0'],
        ['Boot Volume Cost', boot_volume_price || '0'],
        [''],
      ];
      
      // Add daily cost data if available
      const dailyCostHeaders = daily_costs && daily_costs.length > 0 ? 
        [['Daily Cost Breakdown'], ['Date', 'Cost']] : [];
        
      const dailyCostRows = daily_costs && daily_costs.length > 0 ?
        daily_costs.map(day => [day.date, day.cost]) : [];
      
      // Combine all data
      const allRows = [
        ...summary,
        ...dailyCostHeaders,
        ...dailyCostRows
      ];
      
      // Convert to CSV format
      let csvContent = allRows.map(row => {
        return row.map(cell => {
          // Handle string values with commas
          if (typeof cell === 'string' && cell.includes(',')) {
            return `"${cell}"`;
          }
          return cell;
        }).join(',');
      }).join('\n');
      
      // Create download link
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      
      // Set link properties
      link.setAttribute('href', url);
      link.setAttribute('download', `instance-cost-${instance_name}-${dateRangeStr}.csv`);
      link.style.visibility = 'hidden';
      
      // Add to document, click and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log(`Cost details for ${instance_name} downloaded successfully`);
    } catch (error) {
      console.error('Error generating instance cost details:', error);
    }
  }, []);

  // Handle tag filter toggle
  const handleTagFilterToggle = () => {
    setIsTagFilterOpen(!isTagFilterOpen);
    // Focus the tag key input when opening
    if (!isTagFilterOpen) {
      setTimeout(() => {
        tagKeyInputRef.current?.focus();
      }, 100);
    }
  };

  // Handle tag filter key press
  const handleTagKeyPress = (event) => {
    if (event.key === 'Enter') {
      if (newTagKey && !newTagValue) {
        // If only key is entered, move focus to value field
        event.preventDefault();
        const valueInput = event.target.parentElement.parentElement.parentElement.nextElementSibling.querySelector('input');
        valueInput?.focus();
      } else if (newTagKey && newTagValue) {
        // If both fields are filled, add the tag
        handleAddTagFilter();
      }
    }
  };

  return (
    <Box>
      {/* Page Header */}
      <PageHeader
        title="OCI Instances"
        subtitle="Monitor and manage Oracle Cloud Infrastructure instances"
        icon={InstanceIcon}
        actions={
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            {/* Back button */}
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              component={Link}
              to="/oci"
              sx={{
                borderRadius: '12px',
                textTransform: 'none',
                borderWidth: '1.5px',
                padding: '8px 16px',
                '&:hover': {
                  borderWidth: '1.5px',
                  transform: 'translateY(-2px)',
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              Back to Dashboard
            </Button>
            
            {/* Date Range Selector */}
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={(newValue) => setStartDate(newValue)}
                    slotProps={{ textField: { size: 'small' } }}
                    maxDate={endDate}
                  />
                  <DatePicker
                    label="End Date"
                    value={endDate}
                    onChange={(newValue) => setEndDate(newValue)}
                    slotProps={{ textField: { size: 'small' } }}
                    minDate={startDate}
                    maxDate={new Date()}
                  />
                </Box>
                
                {/* Tag Filter Component */}
                <Box>
                  <Button
                    variant="outlined"
                    onClick={handleTagFilterToggle}
                    startIcon={<FilterIcon />}
                    size="small"
                    sx={{
                      borderRadius: '8px',
                      textTransform: 'none',
                      borderWidth: '1.5px',
                      height: '40px',
                      '&:hover': {
                        borderWidth: '1.5px',
                      },
                    }}
                  >
                    Tag Filters {Object.keys(tagFilters).length > 0 && `(${Object.keys(tagFilters).length})`}
                  </Button>

                  <Collapse in={isTagFilterOpen}>
                    <Paper sx={{ 
                      mt: 2, 
                      p: 2, 
                      borderRadius: '12px',
                      boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.05)}`,
                      position: 'absolute',
                      zIndex: 1000,
                      backgroundColor: 'background.paper',
                      minWidth: '500px'
                    }}>
                      <Typography variant="subtitle2" sx={{ mb: 2 }}>Add Tag Filter</Typography>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Tag Key"
                            value={newTagKey}
                            onChange={(e) => setNewTagKey(e.target.value)}
                            onKeyPress={handleTagKeyPress}
                            inputRef={tagKeyInputRef}
                            autoComplete="off"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Tag Value"
                            value={newTagValue}
                            onChange={(e) => setNewTagValue(e.target.value)}
                            onKeyPress={handleTagKeyPress}
                            autoComplete="off"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Button
                            variant="contained"
                            onClick={handleAddTagFilter}
                            disabled={!newTagKey || !newTagValue}
                            sx={{
                              borderRadius: '8px',
                              textTransform: 'none',
                              boxShadow: 'none',
                              '&:hover': {
                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                              }
                            }}
                          >
                            Add Filter
                          </Button>
                        </Grid>
                      </Grid>
                      
                      {Object.keys(tagFilters).length > 0 && (
                        <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {Object.entries(tagFilters).map(([key, value]) => (
                            <Chip
                              key={key}
                              label={`${key}: ${value}`}
                              onDelete={() => handleRemoveTagFilter(key)}
                              color="primary"
                              variant="outlined"
                              sx={{
                                borderRadius: '8px',
                                '& .MuiChip-deleteIcon': {
                                  color: theme.palette.primary.main,
                                }
                              }}
                            />
                          ))}
                        </Box>
                      )}
                    </Paper>
                  </Collapse>
                </Box>

                <Typography variant="caption" color="text.secondary" sx={{ ml: 1, alignSelf: 'center' }}>
                  {`Showing data for: ${formatDateForAPI(startDate)} to ${formatDateForAPI(endDate)} (${stats.dayCount} days)`}
                </Typography>
              </Box>
            </LocalizationProvider>
            
            {/* Export Cost Data Button */}
            <Button
              variant="outlined"
              color="primary"
              startIcon={<ShowChartIcon />}
              onClick={handleDownloadCostTrend}
              disabled={filteredInstances.length === 0}
              sx={{
                borderRadius: '12px',
                textTransform: 'none',
                padding: '8px 16px',
                borderWidth: '1.5px',
                position: 'relative',
                overflow: 'hidden',
                '&:before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: -1,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`,
                  opacity: 0,
                  transition: 'opacity 0.2s ease-in-out',
                },
                '&:hover': {
                  borderWidth: '1.5px',
                  transform: 'translateY(-2px)',
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                  '&:before': {
                    opacity: 1,
                  },
                },
                '&:disabled': {
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                  color: alpha(theme.palette.primary.main, 0.3),
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              Export Cost Trend
            </Button>
            
            {/* Search */}
            <TextField
              placeholder="Search instances..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{ 
                minWidth: 220,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`,
                  },
                  '&.Mui-focused': {
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                    '& fieldset': {
                      borderColor: theme.palette.primary.main,
                      borderWidth: '1.5px',
                    },
                  },
                  '& .MuiInputBase-input': {
                    '&::placeholder': {
                      transition: 'opacity 0.2s ease-in-out',
                    },
                    '&:focus::placeholder': {
                      opacity: 0.7,
                    }
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="primary" />
                  </InputAdornment>
                ),
              }}
            />
            
            {/* Controls wrapper for smaller buttons */}
            <Box display="flex" gap={1}>
              {/* Refresh button */}
              <Tooltip title="Refresh Data">
                <IconButton 
                  onClick={handleRefresh}
                  sx={{
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    borderRadius: '12px',
                    padding: '10px',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.2),
                      transform: 'translateY(-2px)',
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                    },
                    '&:active': {
                      transform: 'translateY(0px)',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              
              {/* View mode toggle */}
              <Tooltip title={viewMode === 'table' ? 'Grid View' : 'Table View'}>
                <IconButton 
                  onClick={() => setViewMode(viewMode === 'table' ? 'grid' : 'table')}
                  sx={{
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    borderRadius: '12px',
                    padding: '10px',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.2),
                      transform: 'translateY(-2px)',
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                    },
                    '&:active': {
                      transform: 'translateY(0px)',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  {viewMode === 'table' ? <GridViewIcon /> : <ListViewIcon />}
                </IconButton>
              </Tooltip>
              
              {/* Download Report button */}
              <Tooltip title="Download Report">
                <IconButton 
                  onClick={handleDownloadReport}
                  disabled={filteredInstances.length === 0}
                  sx={{
                    backgroundColor: 'transparent',
                    borderRadius: '12px',
                    padding: '6px',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.success.main, 0.04),
                      transform: 'translateY(-1px)',
                    },
                    '&:active': {
                      transform: 'translateY(0)',
                    },
                    transition: 'all 0.15s ease-in-out',
                    '&:disabled': {
                      opacity: 0.4,
                      backgroundColor: 'transparent',
                    }
                  }}
                >
                  <DownloadIcon 
                    sx={{ 
                      color: theme.palette.success.main,
                      fontSize: '1.5rem',
                    }} 
                  />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        }
      />
      
      {/* Filters */}
      <Paper 
        sx={{ 
          p: 2, 
          mb: 3,
          borderRadius: '16px',
          boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.05)}`,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center'
        }}
      >
        {/* Region Selector */}
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Region</InputLabel>
          <Select
            value={region}
            label="Region"
            onChange={(e) => setRegion(e.target.value)}
            disabled={loading.regions}
            startAdornment={
              <FilterIcon fontSize="small" sx={{ ml: 1, mr: 0.5, color: theme.palette.text.secondary }} />
            }
          >
            <MenuItem value="">All Regions</MenuItem>
            {regions.map((reg) => (
              <MenuItem key={reg} value={reg}>{reg}</MenuItem>
            ))}
          </Select>
        </FormControl>
        
        {/* Compartment Selector */}
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Compartment</InputLabel>
          <Select
            value={compartment}
            label="Compartment"
            onChange={(e) => setCompartment(e.target.value)}
            disabled={loading.compartments}
            startAdornment={
              <FilterIcon fontSize="small" sx={{ ml: 1, mr: 0.5, color: theme.palette.text.secondary }} />
            }
          >
            <MenuItem value="">All Compartments</MenuItem>
            {compartments.map((comp) => (
              <MenuItem key={comp} value={comp}>{comp}</MenuItem>
            ))}
          </Select>
        </FormControl>
        
        {/* Stats */}
        <Box ml="auto" display="flex" gap={2} flexWrap="wrap">
          <Chip 
            icon={<InstanceIcon fontSize="small" />}
            label={`Total: ${stats.total}`}
            sx={{
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
              fontWeight: 'medium',
              '& .MuiChip-icon': { color: theme.palette.primary.main }
            }}
          />
          <Chip 
            icon={<CheckCircleIcon fontSize="small" />}
            label={`Running: ${stats.running}`}
            sx={{
              backgroundColor: alpha(theme.palette.success.main, 0.1),
              color: theme.palette.success.main,
              fontWeight: 'medium',
              '& .MuiChip-icon': { color: theme.palette.success.main }
            }}
          />
          <Chip 
            icon={<CancelIcon fontSize="small" />}
            label={`Stopped: ${stats.stopped}`}
            sx={{
              backgroundColor: alpha(theme.palette.warning.main, 0.1),
              color: theme.palette.warning.main,
              fontWeight: 'medium',
              '& .MuiChip-icon': { color: theme.palette.warning.main }
            }}
          />
          <Chip 
            icon={<CostIcon fontSize="small" />}
            label={`Total Cost: ${formatCurrency(stats.totalCost)}`}
            title={`For date range: ${stats.dateRange} (${stats.dayCount} days)`}
            sx={{
              backgroundColor: alpha(theme.palette.info.main, 0.1),
              color: theme.palette.info.main,
              fontWeight: 'medium',
              '& .MuiChip-icon': { color: theme.palette.info.main }
            }}
          />
          <Chip 
            icon={<CostIcon fontSize="small" />}
            label={`Daily Avg: ${formatCurrency(stats.avgDailyCost)}`}
            sx={{
              backgroundColor: alpha(theme.palette.secondary.main, 0.1),
              color: theme.palette.secondary.main,
              fontWeight: 'medium',
              '& .MuiChip-icon': { color: theme.palette.secondary.main }
            }}
          />
        </Box>
      </Paper>
      
      {/* Loading Overlay */}
      <LoadingOverlay
        loading={loading.instances}
        message="Loading instance data..."
      >
        {filteredInstances.length === 0 ? (
          <EmptyState 
            title="No instances found"
            message={
              Object.keys(tagFilters).length > 0
                ? "No instances match the current tag filters. Try adjusting your filters or removing some tags."
                : "No instances match the current filter criteria. Try adjusting your filters or search term."
            }
            icon={InstanceIcon}
          />
        ) : viewMode === 'table' ? (
          /* Table View */
          <Paper 
            sx={{ 
              borderRadius: '16px',
              overflow: 'hidden',
              boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.05)}`
            }}
          >
            <TableContainer>
              <Table sx={{ minWidth: 800 }}>
                <TableHead>
                  <TableRow sx={{
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    '& th': {
                      color: theme.palette.text.primary,
                      fontWeight: 500,
                    }
                  }}>
                    <TableCell>Instance Name</TableCell>
                    <TableCell>Region</TableCell>
                    <TableCell>Compartment</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Shape</TableCell>
                    <TableCell align="center">CPUs</TableCell>
                    <TableCell align="center">Memory (GB)</TableCell>
                    <TableCell align="center">Boot Volume (GB)</TableCell>
                    <TableCell align="right">Cost for Date Range</TableCell>
                    <TableCell align="right">Created By</TableCell>
                    <TableCell align="center">Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedInstances.map((instance) => (
                    <TableRow 
                      key={instance._id}
                      hover
                      sx={{
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.03),
                        },
                        '& td': {
                          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
                        }
                      }}
                    >
                      <TableCell>{instance.instance_name}</TableCell>
                      <TableCell>{instance.region_name}</TableCell>
                      <TableCell>{instance.compartment_name}</TableCell>
                      <TableCell>
                        <Chip 
                          label={instance.instance_state} 
                          size="small"
                          color={getStatusColor(instance.instance_state)}
                          sx={{
                            fontWeight: 500,
                            borderRadius: '12px'
                          }}
                        />
                      </TableCell>
                      <TableCell>{instance.vm_shape || 'N/A'}</TableCell>
                      <TableCell align="center">{instance.cpu || 'N/A'}</TableCell>
                      <TableCell align="center">{instance.memory ? instance.memory.toFixed(1) : 'N/A'}</TableCell>
                      <TableCell align="center">{instance.boot_volume_size || 'N/A'}</TableCell>
                      <TableCell align="right">
                        <Tooltip title={
                          <React.Fragment>
                            <Typography variant="caption" display="block">
                              Date range: {formatDateRange(instance.date_range?.start_date, instance.date_range?.end_date)}
                            </Typography>
                            <Typography variant="caption" display="block">
                              Days with data: {instance.days_with_data} of {instance.date_range?.days_count} days
                            </Typography>
                            <Typography variant="caption" display="block">
                              Daily average: {formatCurrency(instance.daily_avg_cost)}
                            </Typography>
                            <Divider sx={{ my: 0.5 }} />
                            <Typography variant="caption" display="block">
                              Compute: {formatCurrency(instance.compute_cost)}
                            </Typography>
                            <Typography variant="caption" display="block">
                              Boot Volume: {formatCurrency(instance.boot_volume_price)}
                            </Typography>
                          </React.Fragment>
                        }>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {formatCurrency(instance.total_cost)}
                            </Typography>
                            {instance.days_with_data > 1 && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                Avg: {formatCurrency(instance.daily_avg_cost)}/day
                              </Typography>
                            )}
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Box display="flex" alignItems="center" justifyContent="flex-end">
                          <PersonIcon fontSize="small" sx={{ mr: 1, color: theme.palette.text.secondary }} />
                          {instance.created_by || 'N/A'}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton 
                          size="small" 
                          color="primary" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenCostDialog(instance);
                          }}
                          title="View cost details"
                        >
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={filteredInstances.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        ) : (
          /* Grid View */
          <Box>
            <Grid container spacing={3}>
              {paginatedInstances.map((instance) => (
                <Grid item xs={12} sm={6} md={4} key={instance._id}>
                  <Card 
                    sx={{ 
                      borderRadius: '16px',
                      height: '100%',
                      boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.05)}`,
                      transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: `0 8px 25px ${alpha(theme.palette.common.black, 0.1)}`,
                      }
                    }}
                  >
                    <CardContent>
                      {/* Instance Header */}
                      <Box display="flex" justifyContent="space-between" mb={2}>
                        <Box>
                          <Typography variant="h6" fontWeight="medium" noWrap>
                            {instance.instance_name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {instance.region_name} / {instance.compartment_name}
                          </Typography>
                        </Box>
                        <Chip 
                          label={instance.instance_state} 
                          size="small"
                          color={getStatusColor(instance.instance_state)}
                          sx={{
                            fontWeight: 500,
                            borderRadius: '12px',
                            height: 24
                          }}
                        />
                      </Box>
                      
                      {/* Instance Details */}
                      <Box>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Box display="flex" alignItems="center" mb={1.5}>
                              <CpuIcon fontSize="small" sx={{ color: theme.palette.primary.main, mr: 1 }} />
                              <Typography variant="body2">
                                {instance.cpu || 'N/A'} CPUs
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={6}>
                            <Box display="flex" alignItems="center" mb={1.5}>
                              <MemoryIcon fontSize="small" sx={{ color: theme.palette.secondary.main, mr: 1 }} />
                              <Typography variant="body2">
                                {instance.memory ? `${instance.memory.toFixed(1)} GB` : 'N/A'}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={6}>
                            <Box display="flex" alignItems="center" mb={1.5}>
                              <StorageIcon fontSize="small" sx={{ color: theme.palette.warning.main, mr: 1 }} />
                              <Typography variant="body2">
                                {instance.boot_volume_size ? `${instance.boot_volume_size} GB` : 'N/A'}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={6}>
                            <Box display="flex" alignItems="center" mb={1.5}>
                              <CostIcon fontSize="small" sx={{ color: theme.palette.success.main, mr: 1 }} />
                              <Tooltip title={
                                <React.Fragment>
                                  <Typography variant="caption" display="block">
                                    Date range: {formatDateRange(instance.date_range?.start_date, instance.date_range?.end_date)}
                                  </Typography>
                                  <Typography variant="caption" display="block">
                                    Days with data: {instance.days_with_data} of {instance.date_range?.days_count} days
                                  </Typography>
                                  <Typography variant="caption" display="block">
                                    Daily average: {formatCurrency(instance.daily_avg_cost)}
                                  </Typography>
                                  <Divider sx={{ my: 0.5 }} />
                                  <Typography variant="caption" display="block">
                                    Compute: {formatCurrency(instance.compute_cost)}
                                  </Typography>
                                  <Typography variant="caption" display="block">
                                    Boot Volume: {formatCurrency(instance.boot_volume_price)}
                                  </Typography>
                                </React.Fragment>
                              }>
                                <Box>
                                  <Typography variant="body2" fontWeight="medium">
                                    {formatCurrency(instance.total_cost)}
                                  </Typography>
                                  {instance.days_with_data > 1 && (
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      Avg: {formatCurrency(instance.daily_avg_cost)}/day
                                    </Typography>
                                  )}
                                </Box>
                              </Tooltip>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                      
                      <Divider sx={{ my: 1.5, opacity: 0.1 }} />
                      
                      {/* Instance Footer */}
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" color="text.secondary">
                          Shape: {instance.vm_shape || 'N/A'}
                        </Typography>
                        <Box display="flex" alignItems="center">
                          <PersonIcon fontSize="small" sx={{ color: theme.palette.text.secondary, mr: 0.5 }} />
                          <Typography variant="caption" color="text.secondary">
                            {instance.created_by || 'N/A'}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            
            {/* Pagination for Grid View */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <TablePagination
                rowsPerPageOptions={[12, 24, 36, 48]}
                component="div"
                count={filteredInstances.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Box>
          </Box>
        )}
      </LoadingOverlay>
      
      {/* Cost details dialog */}
      <CostDetailsDialog />
    </Box>
  );
};

export default OCIInstances;